/**
 * The board (Miro's): every node an item placed freely at its own size (`node.board`), with
 * its own look (`node.style`), and frames (groups with a `board` rectangle) carrying what is
 * inside them. This service keeps what must outlive the graph window: the board's pan and zoom,
 * the tool chosen, the gesture in progress and what others drag, and every edit the board makes.
 *
 * A gesture (move, resize, rotate) is shown at once and to everyone a few times per second
 * (`project.drag_boxes` with `view: "board"`, nothing stored); its end is **one** `graph.batch`
 * of `update_node { board }` / `update_group { board }` (one undo), which also puts a dropped
 * item into the frame it landed in. Everything else (new items, styles, groups, duplicates,
 * pastes, z-order, alignment) is one batch too.
 */
import { createEffect, createMemo, createSignal, on, type Accessor } from "solid-js";
import { unwrap } from "solid-js/store";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Link } from "@protocol/Link";
import type { NodePatch } from "@protocol/NodePatch";
import type { Actions, NewNode } from "../../client/actions";
import type { GraphMirror } from "../../client/graph";
import type { CommandSet } from "../../shared/commands";
import type { MenuSection } from "../../shared/menus";
import { shownWith } from "../../shared/categories";
import { toast } from "../../shared/toast";
import { DEVICES, itemShape } from "../../shared/elements";
import { isRunKey, plainOf, readRich, styleRange, withoutKey, writeRich, type Run } from "../../shared/richText";
import type { KindsService } from "../kinds";
import type { PresenceService } from "../presence";
import type { BoxesDragged, Project } from "../project";
import type { View } from "../view";
import { copyNodes, groupEdits, pasteOf, PASTE_OFFSET, readCopied } from "../clipboard";
import {
  align,
  angleTo,
  asPatch,
  boardOf,
  bounds,
  bottomZ,
  carried,
  defaultSize,
  distribute,
  frameAround,
  normal,
  screensOf,
  resized,
  rounded,
  snap,
  topZ,
  type Alignment,
  type BoardContent,
  type BoardFrame,
  type Guide,
  type Handle,
  type Point,
  type Rect,
} from "./geometry";
import { cropOf, cropped, cropText, inkOf, MIN_SIZE, parseCrop, wholePicture, type Crop } from "./geometry";

export * from "./geometry";

/** The first `length` characters of `runs`. */
function cut(runs: readonly Run[], length: number): Run[] {
  let left = length;
  return runs.flatMap((r) => {
    if (left <= 0) return [];
    const text = r.text.slice(0, left);
    left -= text.length;
    return [{ text, style: r.style }];
  });
}
export * from "./components";

/** How far the board zooms out and in. */
/**
 * How far the board zooms: as far as one likes both ways (the bounds only keep the numbers
 * meaningful); "fit" never goes past 100%.
 */
export const BOARD_ZOOM = { min: 1e-4, max: 1e4, fitMax: 1 } as const;

/** How close (screen pixels) an edge must come to another's to snap to it. */
const SNAP_PX = 6;

/** How long another page's drag is shown without news before it is dropped. */
const STALE_MS = 4000;

/** What a click on the board does. */
export type BoardTool = "select" | "hand" | "note" | "text" | "shape" | "pen" | "eraser" | "button" | "input" | "checkbox" | "list" | "arrow" | "frame" | "image";

/** A tool as the tool bar shows it. */
export interface ToolInfo {
  id: BoardTool;
  label: string;
  key: string;
}

/** Every tool, in the toolbar's order, with its words and key (a drawing tool shows where its kind is offered, with the kind's name). */
export const BOARD_TOOLS: readonly ToolInfo[] = [
  { id: "select", label: "Select", key: "V" },
  { id: "hand", label: "Hand (pan)", key: "H" },
  { id: "frame", label: "Frame", key: "Shift+F" },
  { id: "note", label: "Sticky note", key: "S" },
  { id: "text", label: "Text", key: "T" },
  { id: "shape", label: "Shape", key: "R" },
  { id: "button", label: "Button", key: "B" },
  { id: "input", label: "Input", key: "Shift+I" },
  { id: "checkbox", label: "Checkbox", key: "X" },
  { id: "list", label: "List", key: "Shift+L" },
  { id: "image", label: "Image", key: "I" },
  { id: "pen", label: "Pen (draw by hand)", key: "W" },
  { id: "eraser", label: "Eraser (rubs out drawn lines)", key: "E" },
  { id: "arrow", label: "Arrow", key: "A" },
];

/** The tools that draw a new item (by the word their kind id holds). */
const DRAWN: Partial<Record<BoardTool, RegExp>> = { pen: /drawing/, eraser: /drawing/, note: /note/, text: /text/, shape: /shape|rect/, button: /button/, input: /input/, checkbox: /checkbox/, list: /list/, image: /image/ };

/** A new item's words, by its tool. */
const WORDS: Partial<Record<BoardTool, string>> = { pen: "Drawing", text: "Text", note: "Note", image: "Picture", shape: "Shape", button: "Button", input: "Type here", checkbox: "Option", list: "First\nSecond\nThird" };

/** The pen's widths (board units), thinnest first. */
export const PEN_WIDTHS = [2, 4, 8, 16] as const;

/** The pen's colours. */
export const PEN_COLOURS = ["#1d1b26", "#e03131", "#2f80ff", "#2b8a3e", "#f08c00", "#6a4be0", "#ffffff"] as const;

/** The colours offered for notes, fills and lines (Miro's sticky-note colours first). */
export const BOARD_COLOURS = ["#ffe680", "#ffc2d1", "#c7f0d8", "#cfe3ff", "#e4d7ff", "#ffd8a8", "#ffffff", "#222222", "#6a4be0", "#e03131"] as const;

/** What the service needs. */
export interface BoardDeps {
  graph: GraphMirror;
  view: View;
  project: Project;
  actions: Actions;
  kinds: Pick<KindsService, "nodes" | "suggest" | "links" | "toDefine">;
  /** Who this page is (its own drags come back from the core and are not someone else's). */
  presence: Pick<PresenceService, "you">;
  /** Whether the environment worked in prototypes its board (it lists the Prototype window): screens, devices, where clicks lead. */
  prototypes: Accessor<boolean>;
}

/** A gesture in progress. */
type Gesture =
  | { type: "move"; start: Map<string, BoardPlace>; moved: string[] }
  | { type: "resize"; start: Map<string, BoardPlace>; box: Rect; handle: Handle; ratio?: number }
  | { type: "rotate"; start: Map<string, BoardPlace>; centre: Point };

/** A picture being cropped: the whole picture and the part shown, in board units. */
export interface Cropping {
  id: string;
  whole: Rect;
  box: Rect;
}

/** The board service. */
export interface Board {
  /** Pan and zoom: `screen = world * k + (x, y)`. */
  camera: Accessor<{ x: number; y: number; k: number }>;
  /** Tells the size of the drawing area (CSS pixels). */
  setSize(width: number, height: number): void;
  /** `true` once the view was framed (the first time the board is shown, it fits). */
  framed: boolean;
  /** A screen point (CSS pixels from the area's corner) in board units. */
  toWorld(x: number, y: number): Point;
  /** Zooms by `factor` keeping the screen point `(x, y)` still. */
  zoomAt(x: number, y: number, factor: number): void;
  /** Moves the view by a number of pixels. */
  pan(dx: number, dy: number): void;
  /** Frames everything on the board. */
  fit(): void;
  /** Centres the view on an item or a frame. */
  centerOn(id: string): void;
  /** The tool chosen, and choosing one. */
  tool: Accessor<BoardTool>;
  setTool(tool: BoardTool): void;
  /** The tools offered here (the drawing ones whose kind the environment brings), with their words. */
  tools: Accessor<ToolInfo[]>;
  /** Whether the board makes screens (UI/UX): a frame is a device, a click may lead somewhere. */
  prototypes: Accessor<boolean>;
  /** Prototype mode (Figma's): where each click leads is drawn and set, instead of the design. */
  prototyping: Accessor<boolean>;
  setPrototyping(on: boolean): void;
  /** The screens: top-level frames that are not components, left to right. */
  screens: Accessor<BoardFrame[]>;
  /** A click on item `id` leads to the screen `to` (`"back"`: the screen before; `""`: nowhere), one undo. */
  lead(id: string, to: string): Promise<void>;
  /** The selected frame becomes the screen played first (no other is), one undo. */
  makeStart(): Promise<void>;
  /** The selected frames take a device's size (`shared/elements.ts` `DEVICES`), one undo. */
  setDevice(device: string): Promise<void>;
  /** The selected frame becomes a component (or stops being one), one undo. */
  toggleComponent(): Promise<void>;
  /** An instance of the component `id` beside it (or at `at`), selected; resolves with its id. */
  instance(id: string, at?: Point): Promise<string | null>;
  /** What the board draws now: the graph's places with the gestures in progress over them. */
  content: Accessor<BoardContent>;
  /** The board's arrows (the graph's links between two items shown). */
  arrows: Accessor<Link[]>;
  /** The ids selected: nodes, or one frame. */
  selected: Accessor<string[]>;
  /** Selects `ids` (`add`: toggles them in the selection); an item in a plain group brings its group along. */
  select(ids: string[], add?: boolean): void;
  /** The snapping guides of the move in progress. */
  guides: Accessor<Guide[]>;
  /** Starts moving the selection (with what its frames hold). */
  beginMove(): void;
  /** Moves it by (`dx`, `dy`) board units from where it started; `free` turns snapping off (Alt). */
  moveBy(dx: number, dy: number, free?: boolean): void;
  /** Starts resizing the selection's bounds by one of its handles. */
  beginResize(handle: Handle, ratio?: number): void;
  /** Resizes by (`dx`, `dy`) from where it started; `square` keeps its proportions (`ratio` given at the start, a picture's, else the selection's). */
  resizeBy(dx: number, dy: number, square?: boolean): void;
  /** Starts turning the selection around its centre (one item: its own). */
  beginRotate(): void;
  /** Turns it to face `p`; `stepped`: by 15° (Shift). */
  rotateTowards(p: Point, stepped?: boolean): void;
  /** Ends the gesture: one graph edit (one undo); `false` when nothing changed or the core refused. */
  end(): Promise<boolean>;
  /** Puts what the gesture moved back; nothing is changed. */
  cancel(): boolean;
  /** Whether a gesture is in progress. */
  busy(): boolean;
  /** The pen's colour and width (board units), kept while the page is open. */
  pen: Accessor<{ colour: string; width: number }>;
  /** Changes the pen's colour or width. */
  setPen(pen: Partial<{ colour: string; width: number }>): void;
  /** Puts a line drawn by hand through `points` (board units) on the board with the pen's look; resolves with its id. */
  draw(points: Point[]): Promise<string | null>;
  /** Rubs out the drawn lines `ids` (one undo). */
  erase(ids: string[]): Promise<void>;
  /** The picture being cropped (`null`: none). */
  cropping: Accessor<Cropping | null>;
  /** Starts cropping picture `id` (Miro's double click): the whole picture shows around the part kept. */
  startCrop(id: string): void;
  /** Drags a side or corner of the part kept (`"move"`: slides the picture under it) by (`dx`, `dy`) from where the press began. */
  cropBy(handle: Handle | "move", dx: number, dy: number): void;
  /** Ends a crop drag (what it gives stays shown until the crop ends). */
  cropPressEnd(): void;
  /** While cropping: keeps the whole picture again (still to be applied). */
  resetCrop(): void;
  /** Ends cropping: `keep` writes the part kept (one undo), else nothing changes. */
  endCrop(keep: boolean): Promise<void>;
  /** Shows the selected picture whole again, at the scale it was shown (one undo). */
  uncrop(): Promise<void>;
  /** Draws a new item with `tool` in `rect` (a click: its usual size there); resolves with its id. */
  create(tool: BoardTool, rect: Rect, extra?: Partial<NewNode>): Promise<string | null>;
  /** An arrow from one item to another (one undo). */
  connect(from: string, to: string): Promise<boolean>;
  /** Writes an item's words (a frame's title); empty words leave a text item out of the board. */
  setText(id: string, text: string): Promise<void>;
  /**
   * Writes an item's words with looks of their own (`runs`, `shared/richText.ts`), one undo: its
   * name, its `style.rich`, and the whole-item keys `cleared` (moved down into the words) unset.
   */
  setRich(id: string, runs: readonly Run[], cleared?: readonly string[]): Promise<void>;
  /** The style value `key` of an item, frame or link (`""` when unset). */
  styleOf(target: string | Link, key: string): string;
  /** Sets `key` to `value` on the selection (or the selected link), one undo; `""` unsets it. */
  setStyle(key: string, value: string): Promise<void>;
  /** Gathers the selected items into a plain group (Ctrl+G), one undo. */
  group(): Promise<void>;
  /** Takes the selected group (or the selected items' group) apart, keeping its items. */
  ungroup(): Promise<void>;
  /** Copies of the selection beside it, selected (Ctrl+D), one undo. */
  duplicate(): Promise<void>;
  /** The selection as clipboard text (`null`: nothing selected). */
  copy(): string | null;
  /** Removes the selection: items, and a frame with everything it holds (a cut), one undo. */
  removeSelection(): Promise<boolean>;
  /** What an image of the board holds: the selection (a frame with what it holds), else everything. */
  exported(): { items: string[]; frames: string[] };
  /** Saves {@link Board.exported} as a picture (PNG), drawn by the board view shown. */
  exportImage(): Promise<void>;
  /** The board view shown hands over how it draws a picture (`null`: it is gone). */
  setExporter(draw: ((what: { items: string[]; frames: string[] }) => Promise<void>) | null): void;
  /** Adds what `text` holds (from {@link Board.copy}, or nodes copied in another view) around `at` (none: beside where it was), one undo. */
  paste(text: string, at?: Point): Promise<boolean>;
  /** Where an item of kind `kind` made at `at` goes: its usual size centred there, on top, in the frame there. */
  placeFor(kind: string, at: Point): { board: BoardPlace; group?: string };
  /** Lines the selection up (one undo). */
  align(how: Alignment): Promise<void>;
  /** Spreads the selection out evenly (one undo). */
  distribute(axis: "x" | "y"): Promise<void>;
  /** Brings the selection to the front, or sends it to the back (one undo). */
  order(where: "front" | "back"): Promise<void>;
}

/** Creates the board service. */
export function createBoard({ graph, view, project, actions, kinds, presence, prototypes }: BoardDeps): Board {
  const [camera, setCamera] = createSignal({ x: 0, y: 0, k: 1 });
  const [tool, setTool] = createSignal<BoardTool>("select");
  const [prototypingOn, setPrototyping] = createSignal(false);
  const prototyping = () => prototypes() && prototypingOn();
  const [guides, setGuides] = createSignal<Guide[]>([]);
  /** This page's gesture: the places it gives, winning over everything until its edit is in. */
  const [live, setLive] = createSignal<Map<string, BoardPlace>>(new Map());
  /** Other pages' drags, by who drags. */
  const [others, setOthers] = createSignal<Map<string, { places: Map<string, Partial<BoardPlace>>; at: number }>>(new Map());
  let size = { w: 800, h: 600 };
  let gesture: Gesture | null = null;
  /** Whether a gesture is in progress (the style bar hides meanwhile). */
  const [busy, setBusy] = createSignal(false);
  /** Starts or ends a gesture. */
  const setGesture = (next: Gesture | null) => {
    gesture = next;
    setBusy(next !== null);
  };

  /** The board's graph, with the hidden categories left out. */
  const base = createMemo(() => {
    const g = graph.state.graph;
    const hidden = view.state.hiddenCategories;
    const nodes = hidden.length ? g.nodes.filter((n) => shownWith(n.categories, hidden)) : g.nodes;
    return boardOf({ ...g, nodes });
  });

  const content = createMemo<BoardContent>(() => {
    const b = base();
    const mine = live();
    const theirs = others();
    if (!mine.size && !theirs.size) return b;
    const over = new Map<string, BoardPlace>();
    for (const { places } of theirs.values()) for (const [id, p] of places) if (b.places.has(id)) over.set(id, { ...b.places.get(id)!, ...p });
    for (const [id, p] of mine) over.set(id, p);
    const places = new Map(b.places);
    for (const [id, p] of over) places.set(id, p);
    return {
      frames: b.frames.map((f) => (over.has(f.id) ? { ...f, place: over.get(f.id)! } : f)),
      items: b.items.map((i) => (over.has(i.id) ? { ...i, place: over.get(i.id)! } : i)),
      places,
    };
  });

  const arrows = createMemo(() => {
    const places = content().places;
    return graph.state.graph.links.filter((l) => places.has(l.from) && places.has(l.to));
  });

  const selected = createMemo<string[]>(() => {
    const s = view.state.selection;
    if (s?.type === "node") return [s.id];
    if (s?.type === "nodes") return [...s.ids];
    if (s?.type === "group") return [s.id];
    return [];
  });

  const isFrame = (id: string) => base().frames.some((f) => f.id === id);
  const nodeOf = (id: string) => graph.state.graph.nodes.find((n) => n.id === id);
  const groupOf = (id: string) => graph.state.graph.groups.find((g) => g.id === id);

  /** The places the selection (with what its frames carry) holds now. */
  const startPlaces = (ids: string[]) => {
    const places = content().places;
    return new Map(ids.filter((id) => places.has(id)).map((id) => [id, places.get(id)!]));
  };

  /** Shows `places` at once and sends them to the others. */
  const show = (places: Map<string, BoardPlace>, sized: boolean) => {
    setLive(places);
    const positions: Record<string, [number, number]> = {};
    const sizes: Record<string, [number, number]> = {};
    for (const [id, p] of places) {
      positions[id] = [Math.round(p.x), Math.round(p.y)];
      if (sized) sizes[id] = [Math.round(p.w), Math.round(p.h)];
    }
    project.dragBoxes(positions, false, "board", sized ? sizes : undefined);
  };

  /** One batch, ending the drag the others see whatever the core says. */
  const send = async (edits: GraphEdit[]) => (edits.length ? !!(await actions.batch(edits)) : false);

  /** The edits writing `places` (and, for what was dropped, the frame it landed in). */
  const placeEdits = (places: Map<string, BoardPlace>, dropped: string[] = []): GraphEdit[] => {
    const g = unwrap(graph.state.graph);
    const moving = new Set(places.keys());
    const landed = { ...content(), places: new Map([...content().places, ...places]) };
    landed.frames = landed.frames.map((f) => ({ ...f, place: landed.places.get(f.id)! }));
    const edits: GraphEdit[] = [];
    /** The patches of the nodes written, by id (a layout frame's may add to them). */
    const patches = new Map<string, Record<string, unknown>>();
    /** Where the nodes written end up: their new frame, or the one they were in. */
    const homes = new Map<string, string | null>();
    for (const [id, place] of places) {
      const node = g.nodes.find((n) => n.id === id);
      const patch: Record<string, unknown> = { board: asPatch(place) };
      if (node && dropped.includes(id) && (!node.group || isFrame(node.group))) {
        const into = frameAround(landed, place, moving);
        if ((into ?? null) !== (node.group ?? null)) patch.group = into ?? "";
      }
      if (node) {
        homes.set(id, patch.group === undefined ? (node.group ?? null) : (patch.group as string) || null);
        patches.set(id, patch);
        edits.push({ edit: "update_node", id, patch });
      }
      else {
        const group = g.groups.find((x) => x.id === id);
        if (!group) continue;
        if (dropped.includes(id)) {
          const into = frameAround(landed, place, new Set([id, ...carried(g, [id])]));
          if ((into ?? null) !== (group.parent ?? null) && (!group.parent || isFrame(group.parent))) patch.parent = into ?? "";
        }
        edits.push({ edit: "update_group", id, patch });
      }
    }
    for (const frame of new Set([...homes.values()].filter((f): f is string => !!f && laysOut(f)))) edits.push(...relayEdits(frame, places, homes, patches));
    return edits;
  };

  /** Whether frame `id` lays out what it holds (its `style.layout`). */
  const laysOut = (id: string, style = groupOf(id)?.style) => !!style?.layout && !!groupOf(id)?.board;

  /**
   * The edits keeping what a layout frame holds where the layout shows it: `places` (a gesture's,
   * by id) decide the order with everything else where it is shown, `homes` the frame each moved
   * node ends in, `style` the frame's style if it changes. Kept places are then what is shown, so
   * the next drag past an item reorders as seen. Written into `patches` for the nodes already
   * edited, else as edits of their own.
   */
  const relayEdits = (frame: string, places: Map<string, BoardPlace>, homes: Map<string, string | null> = new Map(), patches: Map<string, Record<string, unknown>> = new Map(), style?: { [key: string]: string }): GraphEdit[] => {
    const g = unwrap(graph.state.graph);
    const shown = content().places;
    const nodes = g.nodes.map((n) => {
      const group = homes.has(n.id) ? homes.get(n.id)! : (n.group ?? null);
      const board = places.get(n.id) ?? (group === frame ? shown.get(n.id) : undefined) ?? n.board;
      return { ...n, group, ...(board ? { board } : {}) };
    });
    const groups = g.groups.map((x) => ({ ...x, ...(places.has(x.id) ? { board: places.get(x.id)! } : {}), ...(style && x.id === frame ? { style: { ...x.style, ...style } } : {}) }));
    const laid = boardOf({ ...g, nodes, groups }).places;
    const edits: GraphEdit[] = [];
    for (const n of nodes) {
      const place = n.group === frame ? laid.get(n.id) : undefined;
      if (!place) continue;
      const board = asPatch({ ...place, z: n.board?.z });
      const patch = patches.get(n.id);
      if (patch) patch.board = board;
      else if (JSON.stringify(board) !== JSON.stringify(nodeOf(n.id)?.board ? asPatch(nodeOf(n.id)!.board!) : null)) edits.push({ edit: "update_node", id: n.id, patch: { board } });
    }
    return edits;
  };

  /** The kind a drawing tool makes here: the environment's kind holding the tool's word, else the first. */
  const kindFor = (t: BoardTool): string | null => {
    const offered = kinds.nodes();
    const word = DRAWN[t];
    return (word ? offered.find((k) => word.test(k.id)) : undefined)?.id ?? offered[0]?.id ?? null;
  };

  const tools = createMemo<ToolInfo[]>(() => {
    const offered = kinds.nodes();
    const links = kinds.links().length > 0;
    return BOARD_TOOLS.flatMap((t) => {
      const word = DRAWN[t.id];
      if (!word) return t.id === "arrow" && !links ? [] : t.id === "frame" && prototypes() ? [{ ...t, label: "Screen (frame)" }] : [t];
      const kind = offered.find((k) => word.test(k.id));
      // The pen and eraser keep their own words.
      return kind ? [{ ...t, label: t.id === "pen" || t.id === "eraser" ? t.label : kind.label }] : [];
    });
  });

  const screens = createMemo(() => screensOf(content()));

  /** The edits making the frames `ids` take `device`'s size, keeping their corner. */
  const deviceEdits = (ids: string[], device: string): GraphEdit[] => {
    const size = DEVICES[device];
    return ids.flatMap((id) => {
      const place = content().places.get(id);
      if (!place || !groupOf(id)) return [];
      return [{ edit: "update_group", id, patch: { style: { device }, ...(size ? { board: asPatch({ ...place, w: size.w, h: size.h }) } : {}) } } satisfies GraphEdit];
    });
  };

  /** Items added in one batch, defining their kinds first when a plugin brings them (then `first`: groups they go in); resolves with their ids. */
  const addAll = async (nodes: NewNode[], links: Link[] = [], first: GraphEdit[] = []): Promise<string[] | null> => {
    const define = kinds.toDefine([...new Set(nodes.map((n) => n.kind))], [...new Set(links.map((l) => l.kind))]);
    return actions.addNodes(nodes, links, [...define, ...first]);
  };

  /** The groups `ids` and every group inside them. */
  const groupsWithin = (ids: string[]) => {
    const inside = new Set(ids.filter((id) => !!groupOf(id)));
    for (let grew = true; grew; ) {
      grew = false;
      for (const g of graph.state.graph.groups) if (g.parent && inside.has(g.parent) && !inside.has(g.id)) inside.add(g.id), (grew = true);
    }
    return [...inside];
  };

  /** How the board view shown draws a picture. */
  let exporter: ((what: { items: string[]; frames: string[] }) => Promise<void>) | null = null;

  /** The selected node ids (a frame selected: none). */
  const selectedNodes = () => selected().filter((id) => !!nodeOf(id));

  /** The plain group (one without a frame) the items `ids` all belong to, if any. */
  const plainGroupOf = (ids: string[]) => {
    const first = ids[0] && nodeOf(ids[0])?.group;
    if (!first || isFrame(first) || !ids.every((id) => nodeOf(id)?.group === first)) return null;
    return first;
  };

  const [cropping, setCropping] = createSignal<Cropping | null>(null);
  const [pen, setPenSignal] = createSignal<{ colour: string; width: number }>({ colour: PEN_COLOURS[0], width: PEN_WIDTHS[1] });
  /** Where a crop drag began. */
  let cropStart: Cropping | null = null;

  const board: Board = {
    camera,
    setSize(w, h) {
      size = { w, h };
    },
    framed: false,
    toWorld(x, y) {
      const c = camera();
      return { x: (x - c.x) / c.k, y: (y - c.y) / c.k };
    },
    zoomAt(x, y, factor) {
      const c = camera();
      const k = Math.min(BOARD_ZOOM.max, Math.max(BOARD_ZOOM.min, c.k * factor));
      setCamera({ k, x: x - ((x - c.x) * k) / c.k, y: y - ((y - c.y) * k) / c.k });
    },
    pan(dx, dy) {
      const c = camera();
      setCamera({ ...c, x: c.x + dx, y: c.y + dy });
    },
    fit() {
      const all = bounds([...content().places.values()]);
      board.framed = true;
      if (!all) return void setCamera({ x: size.w / 2, y: size.h / 2, k: 1 });
      const margin = 60;
      const k = Math.min(BOARD_ZOOM.fitMax, Math.max(BOARD_ZOOM.min, Math.min((size.w - margin * 2) / all.w, (size.h - margin * 2) / all.h)));
      setCamera({ k, x: size.w / 2 - (all.x + all.w / 2) * k, y: size.h / 2 - (all.y + all.h / 2) * k });
    },
    centerOn(id) {
      const p = content().places.get(id);
      if (!p) return;
      const k = camera().k;
      setCamera({ k, x: size.w / 2 - (p.x + p.w / 2) * k, y: size.h / 2 - (p.y + p.h / 2) * k });
    },
    tool,
    setTool,
    tools,
    prototypes,
    prototyping,
    setPrototyping(on) {
      setPrototyping(on);
      if (on) setTool("select");
    },
    screens,
    async lead(id, to) {
      if (!nodeOf(id)) return;
      await actions.updateNode(id, { style: { navigate: to, ...(to ? {} : { transition: "" }) } });
    },
    async makeStart() {
      const id = selected()[0];
      if (!id || !screens().some((f) => f.id === id)) return void toast("Select a screen (a frame at the top level) to start the prototype there.");
      const edits: GraphEdit[] = screens().flatMap((f) => (f.id === id ? [{ edit: "update_group", id, patch: { style: { start: "yes" } } }] : f.group.style?.start ? [{ edit: "update_group", id: f.id, patch: { style: { start: "" } } }] : []));
      await send(edits);
    },
    async setDevice(device) {
      await send(deviceEdits(selected().filter(isFrame), device));
    },
    async toggleComponent() {
      const id = selected()[0];
      if (!id || !isFrame(id)) return void toast("Select a frame to make it a component.");
      const on = groupOf(id)?.style?.component === "yes";
      await actions.updateGroup(id, { style: { component: on ? "" : "yes", ...(on ? {} : { start: "", device: "" }) } });
      toast(on ? "It is a frame again; its instances show nothing." : "It is a component: right-click it to make an instance, which follows every change made to it.");
    },
    async instance(id, at) {
      const frame = content().frames.find((f) => f.id === id);
      const kind = kinds.nodes().find((k) => /instance/.test(k.id))?.id;
      if (!frame || !kind) return null;
      const { w, h } = frame.place;
      const corner = at ? { x: at.x - w / 2, y: at.y - h / 2 } : { x: frame.place.x + w + 40, y: frame.place.y };
      const place = rounded({ ...corner, w, h, z: topZ(content()) + 1 });
      const into = frameAround(content(), place, new Set([id, ...carried(unwrap(graph.state.graph), [id])]));
      const ids = await addAll([{ name: frame.group.name, kind, categories: [], board: place, style: { component: id }, ...(into ? { group: into } : {}) }]);
      const made = ids?.[0] ?? null;
      if (made) view.selectNode(made);
      return made;
    },
    content,
    arrows,
    selected,
    select(ids, add = false) {
      // An item in a plain group is picked with its whole group, as Miro does.
      const widened = ids.flatMap((id) => {
        const group = nodeOf(id)?.group;
        if (!group || isFrame(group) || add) return [id];
        return graph.state.graph.nodes.filter((n) => n.group === group).map((n) => n.id);
      });
      if (widened.length === 1 && isFrame(widened[0]!)) return view.selectGroup(widened[0]!);
      const nodes = widened.filter((id) => !isFrame(id));
      if (!add) return view.selectNodes(nodes);
      const now = new Set(selectedNodes());
      for (const id of nodes) now.has(id) ? now.delete(id) : now.add(id);
      view.selectNodes([...now]);
    },
    guides,
    beginMove() {
      const g = unwrap(graph.state.graph);
      const chosen = selected();
      setGesture({ type: "move", start: startPlaces(carried(g, chosen)), moved: chosen });
    },
    moveBy(dx, dy, free = false) {
      if (gesture?.type !== "move") return;
      const start = gesture.start;
      let [sx, sy] = [dx, dy];
      if (!free) {
        const box = bounds(gesture.moved.map((id) => start.get(id)!).filter(Boolean));
        if (box) {
          const others = content().items.filter((i) => !start.has(i.id)).map((i) => i.place);
          const frames = content().frames.filter((f) => !start.has(f.id)).map((f) => f.place);
          const s = snap({ ...box, x: box.x + dx, y: box.y + dy }, [...others, ...frames], SNAP_PX / camera().k);
          [sx, sy] = [dx + s.dx, dy + s.dy];
          setGuides(s.guides);
        }
      } else setGuides([]);
      show(new Map([...start].map(([id, p]) => [id, { ...p, x: p.x + sx, y: p.y + sy }])), false);
    },
    beginResize(handle, ratio) {
      const start = startPlaces(selected());
      const box = bounds([...start.values()]);
      if (box) setGesture({ type: "resize", start, box, handle, ratio });
    },
    resizeBy(dx, dy, square = false) {
      if (gesture?.type !== "resize") return;
      show(resized(gesture.start, gesture.box, gesture.handle, dx, dy, square, gesture.ratio ?? gesture.box.w / gesture.box.h), true);
    },
    pen,
    setPen(next) {
      setPenSignal({ ...pen(), ...next });
    },
    async draw(points) {
      const { colour, width } = pen();
      // Simplified within half a screen pixel, so it looks the same and stays small.
      const ink = inkOf(points, width, 0.5 / camera().k);
      if (!ink) return null;
      const kind = kindFor("pen");
      if (!kind || !/drawing/.test(kind)) return void toast("Turn on a plugin that brings drawings (Art) to draw by hand.", "error"), null;
      const place = rounded({ ...ink.place, z: topZ(content()) + 1 });
      const frame = frameAround(content(), place);
      const ids = await addAll([{ name: "Drawing", kind, categories: [], board: place, style: { path: ink.path, stroke: colour, width: String(width) }, ...(frame ? { group: frame } : {}) }]);
      return ids?.[0] ?? null;
    },
    async erase(ids) {
      const drawn = ids.filter((id) => /drawing/.test(nodeOf(id)?.kind ?? ""));
      if (drawn.length) await actions.removeNodes(drawn);
    },
    cropping,
    startCrop(id) {
      const node = nodeOf(id);
      const place = content().places.get(id);
      if (!node || !place || !/image/.test(node.kind)) return;
      if (place.rotate) return void toast("Turn the picture back straight to crop it.");
      view.selectNode(id);
      const whole = wholePicture(place, parseCrop(node.style?.crop));
      setCropping({ id, whole, box: { x: place.x, y: place.y, w: place.w, h: place.h } });
    },
    cropBy(handle, dx, dy) {
      const now = cropping();
      if (!now) return;
      cropStart ??= now;
      const next = cropped(cropStart.whole, cropStart.box, handle, dx, dy);
      setCropping({ id: now.id, ...next });
    },
    cropPressEnd() {
      cropStart = null;
    },
    resetCrop() {
      const now = cropping();
      if (now) setCropping({ ...now, box: { ...now.whole } });
    },
    async endCrop(keep) {
      const now = cropping();
      cropStart = null;
      setCropping(null);
      if (!keep || !now) return;
      const place = content().places.get(now.id);
      if (!place) return;
      const crop: Crop = cropOf(now.whole, now.box);
      const box = rounded({ ...place, ...now.box });
      const text = cropText(crop);
      if (text === (nodeOf(now.id)?.style?.crop ?? "") && box.x === place.x && box.y === place.y && box.w === place.w && box.h === place.h) return;
      await actions.updateNode(now.id, { board: asPatch(box), style: { crop: text } });
    },
    async uncrop() {
      const id = selectedNodes()[0];
      const node = id ? nodeOf(id) : undefined;
      const place = id ? content().places.get(id) : undefined;
      if (!id || !node || !place || !parseCrop(node.style?.crop)) return;
      const whole = wholePicture(place, parseCrop(node.style?.crop));
      await actions.updateNode(id, { board: asPatch(rounded({ ...place, ...whole })), style: { crop: "" } });
    },
    beginRotate() {
      const start = startPlaces(selectedNodes());
      const box = bounds([...start.values()]);
      if (box) setGesture({ type: "rotate", start, centre: { x: box.x + box.w / 2, y: box.y + box.h / 2 } });
    },
    rotateTowards(p, stepped = false) {
      if (gesture?.type !== "rotate") return;
      const angle = angleTo(gesture.centre, p, stepped);
      const { centre } = gesture;
      const turn = (angle * Math.PI) / 180;
      const one = gesture.start.size === 1;
      show(
        new Map(
          [...gesture.start].map(([id, place]) => {
            if (one) return [id, { ...place, rotate: angle }];
            // Several items turn around the selection's centre, each also turning itself.
            const [cx, cy] = [place.x + place.w / 2 - centre.x, place.y + place.h / 2 - centre.y];
            const [nx, ny] = [cx * Math.cos(turn) - cy * Math.sin(turn), cx * Math.sin(turn) + cy * Math.cos(turn)];
            return [id, { ...place, x: centre.x + nx - place.w / 2, y: centre.y + ny - place.h / 2, rotate: (place.rotate ?? 0) + angle }];
          }),
        ),
        false,
      );
    },
    async end() {
      const done = gesture;
      setGesture(null);
      setGuides([]);
      const places = live();
      project.dragBoxes({}, true, "board");
      if (!done || !places.size) return false;
      const changed = new Map([...places].filter(([id, p]) => JSON.stringify(rounded(p)) !== JSON.stringify(rounded(done.start.get(id) ?? p)) || !nodeOf(id)?.board));
      if (!changed.size) {
        setLive(new Map());
        return false;
      }
      const ok = await send(placeEdits(changed, done.type === "move" ? done.moved : []));
      // The graph has the places now (or kept the old ones): stop drawing the gesture's.
      setLive(new Map());
      return ok;
    },
    cancel() {
      if (!gesture) return false;
      setGesture(null);
      setGuides([]);
      setLive(new Map());
      project.dragBoxes({}, true, "board");
      return true;
    },
    busy,
    async create(t, rect, extra = {}) {
      const r = normal(rect);
      const content_ = content();
      if (t === "frame") {
        // A click: a phone's screen where the board makes screens, else Miro's frame.
        const device = prototypes() ? DEVICES.phone! : null;
        const place = rounded(r.w > 8 && r.h > 8 ? r : { ...r, w: device?.w ?? 800, h: device?.h ?? 600 });
        const id = `frame-${Math.random().toString(36).slice(2, 8)}`;
        const look = prototypes() ? { fill: "#ffffff", ...(r.w > 8 && r.h > 8 ? {} : { device: "phone" }) } : {};
        const around = { ...content_, places: content_.places };
        const parent = frameAround(around, place);
        const members = content_.items.filter((i) => {
          const p = i.place;
          return p.x >= place.x && p.y >= place.y && p.x + p.w <= place.x + place.w && p.y + p.h <= place.y + place.h && (!i.node.group || isFrame(i.node.group));
        });
        const ok = await send([
          { edit: "add_group", id, name: `${prototypes() && !parent ? "Screen" : "Frame"} ${content_.frames.length + 1}`, parent, members: members.map((m) => m.id), note: "" },
          { edit: "update_group", id, patch: { board: asPatch(place), ...(Object.keys(look).length ? { style: look } : {}) } },
        ]);
        if (!ok) return null;
        view.selectGroup(id);
        return id;
      }
      const kind = kindFor(t);
      if (!kind) return void toast("Turn on a plugin that brings board items (Art) to draw here.", "error"), null;
      const usual = defaultSize(kind);
      const place = rounded({ ...(r.w > 8 && r.h > 8 ? r : { x: r.x - usual.w / 2, y: r.y - usual.h / 2, ...usual }), z: topZ(content_) + 1 });
      const frame = frameAround(content_, place);
      const style: Record<string, string> = t === "note" ? { fill: BOARD_COLOURS[0] } : t === "shape" ? { shape: "rectangle", fill: "#cfe3ff" } : {};
      const ids = await addAll([{ name: WORDS[t] ?? "Shape", kind, categories: [], board: place, style, ...(frame ? { group: frame } : {}), ...extra }]);
      const id = ids?.[0] ?? null;
      if (id) view.selectNode(id);
      return id;
    },
    async connect(from, to) {
      if (from === to) return false;
      const a = nodeOf(from);
      const b = nodeOf(to);
      if (!a || !b) return void toast("Arrows join two items (not frames).", "error"), false;
      const kind = kinds.suggest(a.kind, b.kind) ?? kinds.links()[0]?.id;
      if (!kind) return void toast("No kind of arrow is on here.", "error"), false;
      return !!(await actions.addLink(from, kind, to));
    },
    async setText(id, text) {
      const words = text.trim();
      const node = nodeOf(id);
      const group = groupOf(id);
      if (node) {
        if (words === node.name) return;
        if (!words) {
          if (/text/.test(node.kind)) await actions.removeNode(id);
          return;
        }
        await actions.updateNode(id, { name: text.replace(/\s+$/, "") });
      } else if (group && words && words !== group.name) await actions.updateGroup(id, { name: words });
    },
    styleOf(target, key) {
      if (typeof target !== "string") return graph.state.graph.links.find((l) => l.from === target.from && l.to === target.to && l.kind === target.kind)?.style?.[key] ?? "";
      return (nodeOf(target)?.style ?? groupOf(target)?.style)?.[key] ?? "";
    },
    async setRich(id, runs, cleared = []) {
      const node = nodeOf(id);
      if (!node) return;
      const text = plainOf(runs).replace(/\s+$/, "");
      if (!text.trim()) {
        if (/text/.test(node.kind)) await actions.removeNode(id);
        return;
      }
      // Spaces and line breaks after the last word go, as with plain words.
      const rich = writeRich(cut(runs, text.length));
      const style: Record<string, string> = Object.fromEntries(cleared.map((k) => [k, ""]));
      if (rich !== (node.style?.rich ?? "")) style.rich = rich;
      const patch: NodePatch = { ...(text !== node.name ? { name: text } : {}), ...(Object.keys(style).length ? { style } : {}) };
      if (Object.keys(patch).length) await actions.updateNode(id, patch);
    },
    async setStyle(key, value) {
      const s = view.state.selection;
      if (s?.type === "link") return void (await actions.updateLink(s.link, { style: { [key]: value } }));
      const edits: GraphEdit[] = selected().map((id): GraphEdit => {
        const node = nodeOf(id);
        if (!node) return { edit: "update_group", id, patch: { style: { [key]: value } } };
        // Words with looks of their own: the whole item's look wins again for this key (a highlight is only ever the words').
        if (isRunKey(key) && (node.style?.rich || key === "highlight")) {
          const runs = readRich(node.style?.rich, node.name);
          const rich = writeRich(key === "highlight" ? styleRange(runs, 0, plainOf(runs).length, key, value) : withoutKey(runs, key));
          if (key === "highlight") return { edit: "update_node", id, patch: { style: { rich } } };
          return { edit: "update_node", id, patch: { style: { [key]: value, rich } } };
        }
        // Free text grows and shrinks with its words' size, so bigger words are not cut off.
        const place = content().places.get(id);
        if (key === "size" && place && itemShape(node.kind) === "text") {
          const before = Number(node.style?.size) || 18;
          const after = Number(value) || 18;
          const h = Math.max(MIN_SIZE, Math.round((place.h * after) / before));
          if (h !== place.h) return { edit: "update_node", id, patch: { style: { [key]: value }, board: asPatch({ ...place, h }) } };
        }
        return { edit: "update_node", id, patch: { style: { [key]: value } } };
      });
      // A frame's layout changing: what it holds is kept where the new layout shows it.
      const layout = ["layout", "gap", "padding", "align"].includes(key);
      for (const id of layout ? selected().filter(isFrame) : []) {
        const style = { ...groupOf(id)?.style, [key]: value };
        if (laysOut(id, style)) edits.push(...relayEdits(id, new Map(), new Map(), new Map(), { [key]: value }));
      }
      await send(edits);
    },
    async group() {
      const ids = selectedNodes();
      if (ids.length < 2) return void toast("Select two items or more to group them.");
      const shared = nodeOf(ids[0]!)?.group ?? null;
      const parent = ids.every((id) => (nodeOf(id)?.group ?? null) === shared) ? shared : null;
      const id = `group-${Math.random().toString(36).slice(2, 8)}`;
      if (await send([{ edit: "add_group", id, name: "Group", parent, members: ids, note: "" }])) toast("Grouped: they move together. Ctrl+Shift+G takes them apart.");
    },
    async ungroup() {
      const s = view.state.selection;
      const id = s?.type === "group" && !isFrame(s.id) ? s.id : plainGroupOf(selectedNodes());
      if (!id) return void toast("Select a group to take it apart.");
      await actions.removeGroup(id);
    },
    async duplicate() {
      const text = board.copy();
      if (text) await board.paste(text);
    },
    copy() {
      // What the board copies is what other views copy too (`state/clipboard.ts`), so items go from one to the other;
      // a selected frame (or group) is copied whole, with what it holds.
      const groups = selected().filter((id) => !!groupOf(id));
      return copyNodes(unwrap(graph.state.graph) as Graph, selectedNodes(), { nodal: () => undefined, diagram: () => undefined, board: (id) => content().places.get(id) }, groups);
    },
    async removeSelection() {
      const groups = groupsWithin(selected());
      const inGroups = new Set(groups);
      const nodes = graph.state.graph.nodes.filter((n) => selected().includes(n.id) || (n.group && inGroups.has(n.group))).map((n) => n.id);
      if (!nodes.length && !groups.length) return false;
      const ok = await send([...(nodes.length ? [{ edit: "remove_nodes", ids: nodes } satisfies GraphEdit] : []), ...groups.map((id): GraphEdit => ({ edit: "remove_group", id }))]);
      if (ok) view.selectNode(null);
      return ok;
    },
    async paste(text, at) {
      const copied = readCopied(text);
      if (!copied) return false;
      const pasted = pasteOf(unwrap(graph.state.graph) as Graph, copied, "board", at ?? null);
      const z = topZ(content()) + 1;
      const pastedGroups = new Set(pasted.groups.map((g) => g.id));
      const nodes = pasted.nodes.map((node, i): NewNode => {
        // Copied in a view with no board place: laid out from where the paste is.
        const usual = defaultSize(node.kind);
        const from = node.board ?? (at ? { x: at.x - usual.w / 2 + i * PASTE_OFFSET, y: at.y - usual.h / 2 + i * PASTE_OFFSET, ...usual } : undefined);
        const place = from ? rounded({ ...from, z: z + i }) : undefined;
        // In a pasted frame it stays there; else it goes into the frame it lands in.
        if (node.group && pastedGroups.has(node.group)) return { ...node, ...(place ? { board: place } : {}) };
        const frame = place ? frameAround(content(), place) : null;
        return { ...node, ...(place ? { board: place } : {}), ...(frame ? { group: frame } : {}) };
      });
      // The top of the pasted groups goes into the frame it lands in, like an item.
      const groups = pasted.groups.map((g) => {
        if (g.parent) return g;
        const members = nodes.filter((n) => n.group === g.id && n.board).map((n) => n.board!);
        const box = g.board ?? bounds(members);
        const into = box ? frameAround(content(), box) : null;
        return into ? { ...g, parent: into } : g;
      });
      const ids = await addAll(nodes, pasted.links, groupEdits(groups));
      if (!ids) return false;
      const tops = groups.filter((g) => !g.parent || !pastedGroups.has(g.parent));
      const loose = ids.filter((_, i) => !(nodes[i]!.group && pastedGroups.has(nodes[i]!.group!)));
      if (tops.length === 1 && !loose.length) view.selectGroup(tops[0]!.id);
      else if (ids.length) view.selectNodes(loose.length ? loose : ids);
      return true;
    },
    exported() {
      const chosen = selected();
      if (!chosen.length) return { items: content().items.map((i) => i.id), frames: content().frames.map((f) => f.id) };
      const groups = new Set(groupsWithin(chosen));
      const items = content().items.filter((i) => chosen.includes(i.id) || (i.node.group && groups.has(i.node.group))).map((i) => i.id);
      return { items, frames: content().frames.filter((f) => groups.has(f.id)).map((f) => f.id) };
    },
    async exportImage() {
      if (!exporter || view.mode() !== "board") return void toast("Show the board to save it as a picture.");
      const what = board.exported();
      if (!what.items.length && !what.frames.length) return void toast("The board is empty: nothing to save as a picture.");
      await exporter(what);
    },
    setExporter(draw) {
      exporter = draw;
    },
    placeFor(kind, at) {
      const usual = defaultSize(kind);
      const place = rounded({ x: at.x - usual.w / 2, y: at.y - usual.h / 2, ...usual, z: topZ(content()) + 1 });
      const frame = frameAround(content(), place);
      return { board: place, ...(frame ? { group: frame } : {}) };
    },
    async align(how) {
      const places = startPlaces(selectedNodes());
      if (places.size < 2) return void toast("Select two items or more to line them up.");
      await send(placeEdits(align(places, how)));
    },
    async distribute(axis) {
      const places = startPlaces(selectedNodes());
      if (places.size < 3) return void toast("Select three items or more to spread them out.");
      await send(placeEdits(distribute(places, axis)));
    },
    async order(where) {
      const ids = selectedNodes();
      if (!ids.length) return;
      const places = startPlaces(ids);
      const sorted = [...places].sort(([, a], [, b]) => (a.z ?? 0) - (b.z ?? 0));
      const from = where === "front" ? topZ(content()) + 1 : bottomZ(content()) - sorted.length;
      await send(placeEdits(new Map(sorted.map(([id, p], i) => [id, { ...p, z: from + i }]))));
    },
  };

  project.onBoxesDragged((dragged: BoxesDragged) => {
    if (dragged.by === presence.you() || dragged.view !== "board") return;
    const next = new Map(others());
    if (dragged.done) next.delete(dragged.by);
    else {
      const places = new Map(next.get(dragged.by)?.places);
      for (const [id, [x, y]] of Object.entries(dragged.positions)) {
        const sized = dragged.sizes?.[id];
        places.set(id, { x, y, ...(sized ? { w: sized[0], h: sized[1] } : {}) });
      }
      next.set(dragged.by, { places, at: Date.now() });
    }
    setOthers(next);
  });
  // On the board, "fit" and "centre on" move the board's view.
  createEffect(on(view.fitRequests, () => view.mode() === "board" && board.fit(), { defer: true }));
  createEffect(on(view.centerRequest, (request) => request && view.mode() === "board" && board.centerOn(request.id), { defer: true }));
  setInterval(() => {
    const now = Date.now();
    const stale = [...others()].filter(([, o]) => now - o.at > STALE_MS);
    if (!stale.length) return;
    const next = new Map(others());
    for (const [by] of stale) next.delete(by);
    setOthers(next);
  }, 1000);
  return board;
}

/** The board's shortcut commands (they do nothing outside the board). */
export function boardCommands(board: Board, view: View): CommandSet {
  /** Runs `then` only while the board is shown. */
  const on = (then: () => unknown) => () => void (view.mode() === "board" && then());
  return {
    "board.tool": (tool) => view.mode() === "board" && board.tools().some((t) => t.id === tool) && board.setTool(tool as BoardTool),
    "board.prototype": on(() => (board.prototypes() ? board.setPrototyping(!board.prototyping()) : toast("Prototype mode is UI/UX's: its frames are screens, and a click may lead from one to another."))),
    "board.duplicate": on(() => board.duplicate()),
    "board.group": on(() => board.group()),
    "board.ungroup": on(() => board.ungroup()),
    "board.front": on(() => board.order("front")),
    "board.back": on(() => board.order("back")),
    "board.export": on(() => board.exportImage()),
  };
}

/** The board's Edit lines (shown only while it is). */
export function boardMenus(board: Board, view: View): MenuSection[] {
  return [
    {
      menu: "edit",
      section: "Board",
      order: 30,
      entries: () => {
        if (view.mode() !== "board") return [];
        const many = board.selected().length > 1;
        return [
          { label: "Duplicate", command: "board.duplicate", disabled: !board.selected().length },
          { label: "Group", command: "board.group", disabled: !many },
          { label: "Ungroup", command: "board.ungroup" },
          { label: "Bring to front", command: "board.front", disabled: !board.selected().length },
          { label: "Send to back", command: "board.back", disabled: !board.selected().length },
          { label: board.selected().length ? "Export the selection as an image…" : "Export the board as an image…", command: "board.export" },
        ];
      },
    },
  ];
}
